import { Bone, Group, MathUtils, type AnimationClip, type Material, type Mesh, type Object3D, SkinnedMesh } from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { addRim } from './materials';
import { bakedFile, processingPlan } from './processing';
import { smoothShade } from './smooth';
import { subdivideModel } from './subdivide';
import { ACCESSORY_SOURCES, ITEM_MODELS, MODEL_ROOT } from './visuals';

export interface ModelTemplate {
  scene: Object3D;
  animations: AnimationClip[];
}

/** Dodatek do przypięcia na kość: obiekt z gotową transformacją względem kości. Klonuj przed użyciem. */
export interface Accessory {
  object: Object3D;
  bone: string;
}

/**
 * Wczytuje modele raz i trzyma je jako szablony do klonowania.
 * Części ciała (osobne SkinnedMesh) scalamy w jedną siatkę na materiał — mniej wywołań rysowania
 * przy setce wrogów na ekranie.
 */
export class Assets {
  private readonly templates = new Map<string, ModelTemplate>();
  private readonly accessories = new Map<string, Accessory>();
  /** Modele już obrobione przez processUnbaked (wczytujemy w dwóch turach: obóz, potem reszta). */
  private readonly processed = new Set<string>();
  // Modele są skompresowane meshoptem (npm run asset:compress); dekoder jest w paczce, bez CDN.
  private readonly loader = new GLTFLoader().setMeshoptDecoder(MeshoptDecoder);

  async load(paths: string[], onProgress?: (done: number, total: number) => void): Promise<void> {
    let done = 0;
    await Promise.all(
      paths.map(async (path) => {
        // Upieczona wersja (npm run asset:bake) jest już zaokrąglona — wczytuje się od razu gotowa.
        const gltf = await this.loader.loadAsync(MODEL_ROOT + (bakedFile(path) ?? path));
        mergeSkinnedParts(gltf.scene);
        // Postacie i ich dodatki dostają światło konturowe.
        const isCharacter = gltf.animations.length > 0;
        gltf.scene.traverse((o) => {
          if ((o as Mesh).isMesh) {
            // Część modeli z potoku AI przychodzi bez normalnych — bez nich światło ich nie oświetla (czarna sylwetka).
            const geo = (o as Mesh).geometry;
            if (!geo.getAttribute('normal')) geo.computeVertexNormals();
            o.castShadow = true;
            o.receiveShadow = true;
            if (isCharacter) addRim((o as Mesh).material as Material);
          }
        });
        this.templates.set(path, { scene: gltf.scene, animations: gltf.animations });
        onProgress?.(++done, paths.length);
      }),
    );
    this.indexAccessories();
  }

  /**
   * Obróbka modeli, które nie zostały upieczone (np. nowe, przed `npm run asset:bake`).
   * Upieczonych nie ruszamy — są już gotowe.
   */
  processUnbaked(): number {
    let count = 0;
    for (const [path, mode] of processingPlan()) {
      if (bakedFile(path) || this.processed.has(path)) continue;
      const t = this.templates.get(path);
      if (!t) continue;
      this.processed.add(path);
      count++;
      if (mode === 'smooth') smoothShade(t.scene);
      else subdivideModel(t.scene, { skinnedOnly: mode === 'subdivide-skinned' });
    }
    return count;
  }

  /** Wygładza cieniowanie wskazanych modeli (dekoracje low-poly). */
  smooth(paths: string[]): void {
    for (const p of new Set(paths)) {
      const t = this.templates.get(p);
      if (t) smoothShade(t.scene);
    }
  }

  /** Zaokrągla bryły wskazanych modeli (podział Loopa). Postacie: tylko ciało, bez broni i dodatków. */
  subdivide(paths: string[], options: { skinnedOnly?: boolean } = {}): void {
    for (const p of new Set(paths)) {
      const t = this.templates.get(p);
      if (t) subdivideModel(t.scene, options);
    }
  }

  template(path: string): ModelTemplate {
    const t = this.templates.get(path);
    if (!t) throw new Error(`Model nie został wczytany: ${path}`);
    return t;
  }

  /** visual: "model:Węzeł" (np. "knight:1H_Sword") albo "item:id" (osobny plik). */
  accessory(visual: string): Accessory | undefined {
    return this.accessories.get(visual);
  }

  private indexAccessories(): void {
    // Dodatki z modeli postaci to zwykłe siatki podpięte pod kość (handslot, head, chest).
    for (const [key, path] of Object.entries(ACCESSORY_SOURCES)) {
      const t = this.templates.get(path);
      if (!t) continue;
      t.scene.traverse((o) => {
        if ((o as Mesh).isMesh && !(o as SkinnedMesh).isSkinnedMesh && o.parent instanceof Bone) {
          this.accessories.set(`${key}:${o.name}`, { object: o, bone: o.parent.name });
        }
      });
    }

    // Osobne modele z pipeline'u: zewnętrzna grupa = obrót i przesunięcie, wewnętrzna = skala i punkt zaczepienia.
    for (const [id, fit] of Object.entries(ITEM_MODELS)) {
      const t = this.templates.get(fit.file);
      if (!t) continue;
      const inner = new Group();
      inner.add(t.scene.clone());
      inner.scale.setScalar(fit.scale);
      inner.position.set(-fit.anchor[0] * fit.scale, -fit.anchor[1] * fit.scale, -fit.anchor[2] * fit.scale);
      const outer = new Group();
      outer.name = `item:${id}`;
      outer.add(inner);
      outer.rotation.set(...(fit.rotation.map((d) => MathUtils.degToRad(d)) as [number, number, number]));
      outer.position.set(...fit.offset);
      this.accessories.set(`item:${id}`, { object: outer, bone: fit.bone });
    }
  }
}

function mergeSkinnedParts(root: Object3D): void {
  const groups = new Map<Material, SkinnedMesh[]>();
  root.traverse((o) => {
    const m = o as SkinnedMesh;
    if (!m.isSkinnedMesh || Array.isArray(m.material)) return;
    const list = groups.get(m.material) ?? [];
    list.push(m);
    groups.set(m.material, list);
  });

  for (const [material, parts] of groups) {
    const first = parts[0];
    if (!first || parts.length < 2 || parts.some((p) => p.skeleton !== first.skeleton || p.parent !== first.parent)) continue;
    const merged = mergeGeometries(parts.map((p) => p.geometry));
    if (!merged) continue; // różne atrybuty — zostawiamy osobne części
    const mesh = new SkinnedMesh(merged, material);
    mesh.name = `${first.name}_merged`;
    mesh.bind(first.skeleton, first.bindMatrix);
    first.parent!.add(mesh);
    for (const p of parts) p.removeFromParent();
  }
}
